import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const AccountSection = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'हिन्दी (Hindi)' },
    { value: 'ta', label: 'தமிழ் (Tamil)' },
    { value: 'te', label: 'తెలుగు (Telugu)' },
    { value: 'bn', label: 'বাংলা (Bengali)' },
    { value: 'mr', label: 'मराठी (Marathi)' }
  ];

  const handleExportData = () => {
    console.log('Exporting user data...');
  };

  const handleDeleteAccount = () => {
    console.log('Account deletion initiated...');
    navigate('/user-login');
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Globe" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Language Preferences</h2>
            <p className="text-sm text-muted-foreground">Choose your preferred language for the interface</p>
          </div>
        </div>

        <Select
          label="Interface Language"
          description="Select the language you want to use for the platform"
          options={languageOptions}
          value={language}
          onChange={setLanguage}
        />
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Download" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Data Export</h2>
            <p className="text-sm text-muted-foreground">Download a copy of your personal data</p>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">What's included?</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Profile information and settings</li>
                <li>• Job applications and history</li>
                <li>• Saved jobs and preferences</li>
                <li>• Messages and communications</li>
              </ul>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          iconName="Download"
          iconPosition="left"
          onClick={handleExportData}
        >
          Request Data Export
        </Button>
      </div>

      <div className="bg-card border border-error rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Delete Account</h2>
            <p className="text-sm text-muted-foreground">Permanently delete your account and all associated data</p>
          </div>
        </div>

        <div className="p-4 bg-error/5 border border-error/20 rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Warning: This action cannot be undone</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Your profile will be permanently deleted</li>
                <li>• All job applications will be withdrawn</li>
                <li>• Saved jobs and preferences will be lost</li>
                <li>• Messages and communications will be deleted</li>
                <li>• You will lose access to all platform features</li>
              </ul>
            </div>
          </div>
        </div>

        <Button
          variant="destructive"
          iconName="Trash2"
          iconPosition="left"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete My Account
        </Button>
      </div>

      {showDeleteModal && (
        <>
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000]" onClick={() => setShowDeleteModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-card border border-border rounded-lg shadow-modal z-[2001] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} color="var(--color-error)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Confirm Account Deletion</h3>
                <p className="text-sm text-muted-foreground">This action is permanent and irreversible</p>
              </div>
            </div>

            <p className="text-sm text-foreground mb-6">
              Are you absolutely sure you want to delete your account? All your data will be permanently removed from our servers and cannot be recovered.
            </p>

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                fullWidth
                iconName="Trash2"
                iconPosition="left"
                onClick={handleDeleteAccount}
              >
                Yes, Delete Account
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountSection;